import React, { useState } from 'react';
import { CheckCircle, XCircle, Ban, UserPlus, Download } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const BulkActionsPanel = ({ selectedCount, onAction }) => {
  const [isConfirming, setIsConfirming] = useState(null);

  const handleAction = (action) => {
    // Actions that need confirmation
    const confirmationActions = ['suspend', 'deactivate', 'delete'];
    
    if (confirmationActions?.includes(action)) {
      setIsConfirming(action);
      return;
    }

    onAction?.(action);
  };

  const confirmAction = (action) => {
    onAction?.(action);
    setIsConfirming(null);
  };

  const cancelAction = () => {
    setIsConfirming(null);
  };

  const getConfirmationMessage = (action) => {
    const messages = {
      suspend: `Are you sure you want to suspend ${selectedCount} staff member${selectedCount !== 1 ? 's' : ''}? They will not be able to work until reactivated.`,
      deactivate: `Are you sure you want to deactivate ${selectedCount} staff member${selectedCount !== 1 ? 's' : ''}?`,
      delete: `Are you sure you want to permanently delete ${selectedCount} staff member${selectedCount !== 1 ? 's' : ''}? This action cannot be undone.`
    };
    return messages?.[action] || '';
  };

  const actions = [
    {
      id: 'activate',
      label: 'Activate',
      icon: CheckCircle,
      className: 'text-green-700 bg-green-50 hover:bg-green-100 border-green-200',
      description: 'Set selected staff as active'
    },
    {
      id: 'deactivate',
      label: 'Deactivate',
      icon: XCircle,
      className: 'text-gray-700 bg-gray-50 hover:bg-gray-100 border-gray-200',
      description: 'Set selected staff as inactive'
    },
    {
      id: 'suspend',
      label: 'Suspend',
      icon: Ban,
      className: 'text-red-700 bg-red-50 hover:bg-red-100 border-red-200',
      description: 'Temporarily suspend selected staff'
    },
    {
      id: 'assign',
      label: 'Assign to Event',
      icon: UserPlus,
      className: 'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-200',
      description: 'Assign selected staff to an event'
    },
    {
      id: 'export',
      label: 'Export',
      icon: Download,
      className: 'text-purple-700 bg-purple-50 hover:bg-purple-100 border-purple-200',
      description: 'Export selected staff data'
    }
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center text-sm text-gray-600">
            <span className="font-medium text-blue-600">{selectedCount}</span>
            <span className="ml-1">staff member{selectedCount !== 1 ? 's' : ''} selected</span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {actions?.map((action) => {
            const Icon = action?.icon;
            return (
              <button
                key={action?.id}
                onClick={() => handleAction(action?.id)}
                className={`
                  flex items-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors
                  ${action?.className}
                `}
                title={action?.description}
              >
                <Icon className="w-4 h-4 mr-2" />
                {action?.label}
              </button>
            );
          })}
        </div>
      </div>
      {/* Confirmation Dialog */}
      {isConfirming && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Action
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              {getConfirmationMessage(isConfirming)}
            </p>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={cancelAction}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmAction(isConfirming)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Confirm {isConfirming}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkActionsPanel;